/**
 * Wix BI Events Service
 *
 * Sends analytics events to Wix Partner Dashboard via the BI Events API.
 * Each event is sent as a separate POST to /apps/v1/bi-event.
 *
 * Features:
 * - Queue with periodic flush (10 seconds)
 * - Per-event sending (Wix API does NOT support batch)
 * - Retry with exponential backoff (3 attempts)
 * - Session storage persistence across page refreshes
 * - Automatic mapping of app events to Wix built-in event names
 * - Silent failures (never breaks the app)
 * - Development mode logging
 *
 * API Reference: https://dev.wix.com/docs/rest/api-reference/app-management/apps/bi-event/send-bi-event
 */

import type {
  WixBiEventPayload,
  WixBiEventsConfig,
} from '../types/wixBiEvents';
import { EVENT_NAME_MAP } from '../types/wixBiEvents';

const DEFAULT_CONFIG: Required<WixBiEventsConfig> = {
  appId: '',
  flushIntervalMs: 10000, // 10 seconds
  maxQueueSize: 50,
  enabled: true,
  debug: false,
};

const API_URL = 'https://www.wixapis.com/apps/v1/bi-event';
const STORAGE_KEY = 'wix_bi_events_queue';
const MAX_RETRIES = 3;
const MAX_QUEUE_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

interface QueuedEvent {
  /** App-level event name (e.g. "blog_generation_initiated") */
  appEventName: string;
  /** Key-value event data */
  eventData?: Record<string, string>;
  timestamp: number;
  retries: number;
}

class WixBiEventsService {
  private config: Required<WixBiEventsConfig>;
  private instanceToken: string | null = null;
  private eventQueue: QueuedEvent[] = [];
  private flushInterval: number | null = null;
  private isFlushing: boolean = false;

  constructor(config?: Partial<WixBiEventsConfig>) {
    this.config = {
      ...DEFAULT_CONFIG,
      ...config,
      appId: config?.appId || import.meta.env.VITE_WIX_APP_ID || '',
      enabled: config?.enabled ?? (import.meta.env.VITE_WIX_BI_EVENTS_ENABLED !== 'false'),
      debug: config?.debug ?? (import.meta.env.VITE_WIX_BI_EVENTS_DEBUG === 'true' || import.meta.env.DEV),
    };

    this.loadQueue();

    if (this.config.enabled) {
      this.startFlushing();
    }

    this.log('WixBiEventsService initialized', { config: this.config });
  }

  /**
   * Initialize with Wix instance token
   */
  initialize(instanceToken: string) {
    this.instanceToken = instanceToken;
    this.log('Initialized with instance token');
  }

  private isReady(): boolean {
    if (!this.config.enabled) {
      this.log('Service disabled');
      return false;
    }

    if (!this.config.appId) {
      this.warn('Wix App ID not configured (VITE_WIX_APP_ID)');
      return false;
    }

    if (!this.instanceToken) {
      this.warn('Instance token not set - call initialize() first');
      return false;
    }

    return true;
  }

  /**
   * Track a BI event.
   * Accepts app-level event names (e.g. "blog_generation_initiated").
   * The service maps them to Wix API format automatically.
   */
  trackEvent(eventName: string, params?: Record<string, unknown>): void {
    if (!this.isReady()) return;

    const eventData = this.stringifyParams(params);

    const event: QueuedEvent = {
      appEventName: eventName,
      eventData,
      timestamp: Date.now(),
      retries: 0,
    };

    this.log('Tracking event', { event });
    this.eventQueue.push(event);
    this.saveQueue();

    if (this.eventQueue.length >= this.config.maxQueueSize) {
      this.log('Queue full, flushing immediately');
      this.flushEvents();
    }
  }

  /**
   * Convert all param values to strings (Wix eventData requires string values)
   */
  private stringifyParams(params?: Record<string, unknown>): Record<string, string> | undefined {
    if (!params) return undefined;

    const result: Record<string, string> = {};

    for (const [key, value] of Object.entries(params)) {
      if (value === null || value === undefined) continue;

      if (typeof value === 'string') {
        result[key] = value;
      } else if (typeof value === 'object') {
        result[key] = JSON.stringify(value);
      } else {
        result[key] = String(value);
      }
    }

    return Object.keys(result).length > 0 ? result : undefined;
  }

  /**
   * Build the Wix API payload from an app-level event
   */
  private buildPayload(event: QueuedEvent): WixBiEventPayload {
    const mapping = EVENT_NAME_MAP[event.appEventName];

    if (mapping) {
      return {
        eventName: mapping.eventName,
        ...(mapping.customEventName && { customEventName: mapping.customEventName }),
        ...(event.eventData && { eventData: event.eventData }),
      };
    }

    // Unmapped events default to CUSTOM
    return {
      eventName: 'CUSTOM',
      customEventName: event.appEventName,
      ...(event.eventData && { eventData: event.eventData }),
    };
  }

  private startFlushing(): void {
    if (this.flushInterval) return;

    this.flushInterval = window.setInterval(() => {
      this.flushEvents();
    }, this.config.flushIntervalMs);

    window.addEventListener('beforeunload', () => {
      this.flushEvents();
    });

    this.log('Started flush interval', { intervalMs: this.config.flushIntervalMs });
  }

  /**
   * Flush queued events — sends each event as a separate POST
   */
  private async flushEvents(): Promise<void> {
    if (this.isFlushing || this.eventQueue.length === 0 || !this.isReady()) return;

    this.isFlushing = true;

    try {
      this.pruneOldEvents();

      // Take events to send (up to maxQueueSize)
      const eventsToSend = this.eventQueue.splice(0, this.config.maxQueueSize);
      this.saveQueue();

      this.log(`Flushing ${eventsToSend.length} events (one request each)`);

      const failedEvents: QueuedEvent[] = [];

      // Send each event individually (Wix API = one event per request)
      for (const event of eventsToSend) {
        const success = await this.sendSingleEvent(event);

        if (!success) {
          event.retries++;
          if (event.retries < MAX_RETRIES) {
            failedEvents.push(event);
          } else {
            this.warn('Event exceeded max retries, dropping', { event });
          }
        }
      }

      // Re-queue failed events for retry
      if (failedEvents.length > 0) {
        this.eventQueue.unshift(...failedEvents);
        this.saveQueue();
        this.log(`Re-queued ${failedEvents.length} failed events for retry`);
      }
    } catch (error) {
      this.error('Error flushing events', error);
    } finally {
      this.isFlushing = false;
    }
  }

  /**
   * Send a single event to Wix BI Events API with retry on 5xx
   */
  private async sendSingleEvent(event: QueuedEvent): Promise<boolean> {
    const payload = this.buildPayload(event);

    try {
      this.log('Sending to Wix → POST /apps/v1/bi-event', { payload });

      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Authorization': this.instanceToken!,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        this.log('Wix API success', { status: response.status, eventName: payload.eventName });
        return true;
      }

      const errorText = await response.text().catch(() => 'Unable to read response');
      this.warn(`Wix API error ${response.status}`, { errorText, payload });

      // Don't retry on 4xx (client error) — something is wrong with the request
      if (response.status >= 400 && response.status < 500) {
        this.error('Client error, not retrying', { status: response.status, errorText });
        return false;
      }

      // 5xx errors will be retried via the queue
      return false;
    } catch (error) {
      this.error('Network error sending event', error);
      return false;
    }
  }

  private pruneOldEvents(): void {
    const cutoffTime = Date.now() - MAX_QUEUE_AGE_MS;
    const originalLength = this.eventQueue.length;
    this.eventQueue = this.eventQueue.filter((event) => event.timestamp > cutoffTime);

    if (this.eventQueue.length < originalLength) {
      this.log(`Pruned ${originalLength - this.eventQueue.length} old events`);
    }
  }

  private loadQueue(): void {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        this.eventQueue = JSON.parse(stored);
        this.log(`Loaded ${this.eventQueue.length} events from storage`);
      }
    } catch (error) {
      this.error('Failed to load queue from storage', error);
    }
  }

  private saveQueue(): void {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.eventQueue));
    } catch (error) {
      this.error('Failed to save queue to storage', error);
    }
  }

  private log(message: string, data?: unknown): void {
    if (this.config.debug) {
      console.log(`[WixBI] ${message}`, data || '');
    }
  }

  private warn(message: string, data?: unknown): void {
    if (this.config.debug) {
      console.warn(`[WixBI] ${message}`, data || '');
    }
  }

  private error(message: string, data?: unknown): void {
    if (this.config.debug) {
      console.error(`[WixBI] ${message}`, data || '');
    }
  }

  destroy(): void {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }
    this.flushEvents();
    this.log('Service destroyed');
  }
}

// Singleton instance
export const wixBiEventsService = new WixBiEventsService();
export default wixBiEventsService;
